// Ponto de parada de uma cidade de passagem SOBRE a rota plotada.
//
// Por que existe: a sugestão de "cidade de passagem" nasce do centroide do
// município (IBGE), que pode estar a 20 km do eixo da rodovia. Esse centroide
// virava (a) o waypoint enviado ao OSRM — fazendo a rota fazer um desvio até o
// centro da cidade — e (b) o pin desenhado no mapa, flutuando longe da linha.
// Este módulo resolve o ponto de parada que está de fato sobre a rota: um ponto
// do traçado DENTRO do polígono do município, de preferência no meio da
// "passagem" da cidade.
//
// Fallback quando a rota não entra no município: o ponto da rota mais próximo
// do centroide, aceito só até `MAX_OFF_ROUTE_FALLBACK_KM`. Passando disso a
// função devolve `null` e quem chama mantém o centroide — um snap para o lado
// errado da cidade é pior que um pin honesto um pouco fora da linha.
//
// Tudo aqui é puro e determinístico: mesma entrada, mesma saída, sem I/O.

import {
  type LatLng,
  cumulativeKmAlong,
  haversineKm,
  pointAtKmAlong,
  projectOnPathKm,
} from "@/lib/route-matching"
import { pointInMunicipality, type MunicipalityDoc } from "@/lib/municipality-polygons"

// `cumulativeKmAlong` e `pointAtKmAlong` moravam aqui; são geometria pura de
// traçado e foram para `lib/route-matching.ts` (que não puxa polígonos IBGE
// nem turf) para o server do chat poder usá-los sem inchar o bundle. O
// re-export mantém os call sites existentes intactos.
export { cumulativeKmAlong, pointAtKmAlong } from "@/lib/route-matching"

export type CityGeometry = MunicipalityDoc["geometry"]

// Acima disso, o ponto mais próximo da rota no centroide é tão longe que
// "parada na cidade" deixa de descrever o que o passageiro encontraria.
export const MAX_OFF_ROUTE_FALLBACK_KM = 8

// Intervalos internos separados por menos que isso são a MESMA passagem: um
// viaduto, um anel, um desvio de 1 km dentro do município não são duas entradas
// na cidade. Sem essa fusão, a densidade de vértices do polyline do OSRM
// (full-res) decide qual pedaço "vence" — a escolha viraria loteria.
export const SPAN_MERGE_GAP_KM = 1.5

// Janela de análise ao longo da rota. `findMunicipalitiesAlongPath` roda isto
// para até MAX_MUNICIPALITIES candidatos sobre o polyline full-res: sem janela,
// seriam 200 × (milhares de pontos × vértices do polígono) — o endpoint
// passaria de segundos. A janela é centrada na projeção do centroide (que o
// chamador já calculou) e é folgada o bastante para conter a passagem inteira
// de qualquer município que caia no corredor (raio máximo de 40 km).
export const CITY_STOP_WINDOW_KM = 60

// 18 -> resolução de ~2^-18 do segmento (sub-centímetro). O polígono do IBGE é
// simplificado, então convergir mais é desperdício de CPU sem mudar o resultado.
const BISECTION_STEPS = 18
const BISECTION_TOLERANCE = 1e-9

export interface CityStopPoint {
  lat: number
  lng: number
  /** Posição do ponto ao longo da rota, em km a partir do início de `path`. */
  alongKm: number
  /** Distância do centroide do município até o ponto escolhido. */
  distanceToCentroidKm: number
  /** `true` quando o ponto está dentro do polígono; `false` no fallback. */
  insideMunicipality: boolean
}

interface InsideSpan {
  fromAlongKm: number
  toAlongKm: number
  lengthKm: number
}

function lerpPoint(a: LatLng, b: LatLng, t: number): LatLng {
  return { lat: a.lat + (b.lat - a.lat) * t, lng: a.lng + (b.lng - a.lng) * t }
}

function isInside(point: LatLng, geometry: CityGeometry): boolean {
  return pointInMunicipality([point.lng, point.lat], geometry)
}

/**
 * Borda do polígono entre um ponto DENTRO e um FORA dele, mais próxima do ponto
 * dentro. Bisseção porque "dentro do polígono" é monotônico ao longo de um
 * segmento reto: com `inside`/`outside` em lados opostos, `t=0` está dentro e
 * `t=1` fora, e o meio classifica um dos dois lados.
 */
function boundaryBetween(inside: LatLng, outside: LatLng, geometry: CityGeometry): LatLng {
  let lo = 0
  let hi = 1
  for (let i = 0; i < BISECTION_STEPS; i++) {
    const mid = (lo + hi) / 2
    if (isInside(lerpPoint(inside, outside, mid), geometry)) lo = mid
    else hi = mid
    if (hi - lo < BISECTION_TOLERANCE) break
  }
  return lerpPoint(inside, outside, lo)
}

/**
 * Trechos do traçado dentro do município, em km ao longo da rota.
 *
 * Os quatro casos de um segmento a→b: dentro/dentro (span inteiro), misto
 * (span do extremo interno até a borda) e fora/fora — que NÃO é ausência de
 * travessia: a rota tipicamente tem só origem e destino, então uma reta que
 * corta a cidade tem as DUAS pontas fora. Nesse caso o teste do ponto médio
 * é o que separa "atravessa" de "passa ao lado".
 */
function insideSpans(path: LatLng[], geometry: CityGeometry, along: number[]): InsideSpan[] {
  const spans: InsideSpan[] = []

  const push = (from: LatLng, to: LatLng, fromAlongKm: number) => {
    const lengthKm = haversineKm(from.lat, from.lng, to.lat, to.lng)
    if (!(lengthKm > 0)) return
    spans.push({ fromAlongKm, toAlongKm: fromAlongKm + lengthKm, lengthKm })
  }

  for (let i = 0; i < path.length - 1; i++) {
    const a = path[i]
    const b = path[i + 1]
    const startAlong = along[i]
    const insideA = isInside(a, geometry)
    const insideB = isInside(b, geometry)

    if (insideA && insideB) {
      push(a, b, startAlong)
      continue
    }
    if (insideA) {
      push(a, boundaryBetween(a, b, geometry), startAlong)
      continue
    }
    if (insideB) {
      const entry = boundaryBetween(b, a, geometry)
      push(entry, b, startAlong + haversineKm(a.lat, a.lng, entry.lat, entry.lng))
      continue
    }

    const mid = lerpPoint(a, b, 0.5)
    if (!isInside(mid, geometry)) continue
    const entry = boundaryBetween(mid, a, geometry)
    const exit = boundaryBetween(mid, b, geometry)
    push(
      entry,
      exit,
      startAlong + haversineKm(a.lat, a.lng, entry.lat, entry.lng)
    )
  }

  return spans
}

/** Funde intervals separados por menos de `gapKm` em uma passagem só. */
function mergeNearbySpans(spans: InsideSpan[], gapKm: number = SPAN_MERGE_GAP_KM): InsideSpan[] {
  if (spans.length === 0) return []
  const merged: InsideSpan[] = [{ ...spans[0] }]
  for (let i = 1; i < spans.length; i++) {
    const prev = merged[merged.length - 1]
    const span = spans[i]
    // `spans` nasce em ordem de percurso, então o gap é a distância entre o
    // fim do anterior e o início do atual.
    if (span.fromAlongKm - prev.toAlongKm <= gapKm) {
      prev.toAlongKm = span.toAlongKm
      prev.lengthKm += span.lengthKm
    } else {
      merged.push({ ...span })
    }
  }
  return merged
}

export interface CityStopPointOptions {
  /** Restringe a análise a esta janela (km) ao redor de `centerAlongKm`. */
  windowKm?: number
  /** `alongKm` da projeção do centroide na rota — centro da janela. */
  centerAlongKm?: number
  /** `cumulativeKmAlong(path)` pré-calculado pelo chamador (evita O(n) repetido). */
  along?: number[]
}

/** Passagem da rota dentro do município, em km absolutos ao longo do traçado. */
export interface CityRouteSpan {
  fromAlongKm: number
  toAlongKm: number
  lengthKm: number
}

interface ScanWindow {
  path: LatLng[]
  along: number[]
}

/**
 * Recorta a janela de análise ao longo da rota. Mantém as referências de km
 * ABSOLUTAS em `along` — é por isso que `pointAtKmAlong` recebe o path completo
 * do chamador e não o recortado.
 */
function scanWindow(path: LatLng[], along: number[], opts: CityStopPointOptions): ScanWindow | null {
  const total = along[along.length - 1]
  if (!(total > 0)) return null

  if (
    !Number.isFinite(opts.centerAlongKm) ||
    typeof opts.windowKm !== "number" ||
    !(opts.windowKm > 0) ||
    total <= 2 * opts.windowKm
  ) {
    return { path, along }
  }

  const center = opts.centerAlongKm as number
  const lo = Math.max(0, center - opts.windowKm)
  const hi = Math.min(total, center + opts.windowKm)
  let startIdx = 0
  for (let i = 0; i < along.length; i++) {
    if (along[i] <= lo) startIdx = i
    else break
  }
  let endIdx = path.length - 1
  for (let i = startIdx; i < along.length; i++) {
    if (along[i] >= hi) {
      endIdx = i
      break
    }
  }
  const windowed = { path: path.slice(startIdx, endIdx + 1), along: along.slice(startIdx, endIdx + 1) }
  if (windowed.path.length < 2) return null
  return windowed
}

function validGeometry(geometry: CityGeometry): boolean {
  return !!geometry && Array.isArray(geometry.coordinates) && geometry.coordinates.length > 0
}

/**
 * Passagens da rota dentro do município, já fundidas.
 *
 * É a base tanto do ponto de parada quanto do refinamento: o diálogo de
 * "escolher um lugar perto" precisa do MESMO trecho que gerou o pin, senão
 * sugere lugares de outro bairro.
 */
export function cityRouteSpans(
  geometry: CityGeometry,
  path: LatLng[],
  opts: CityStopPointOptions = {}
): CityRouteSpan[] {
  if (path.length < 2 || !validGeometry(geometry)) return []
  const along =
    opts.along && opts.along.length === path.length ? opts.along : cumulativeKmAlong(path)
  const window = scanWindow(path, along, opts)
  if (!window) return []
  return mergeNearbySpans(insideSpans(window.path, geometry, window.along))
}

/**
 * Resolve o ponto de parada de uma cidade de passagem sobre a rota.
 *
 * Preferência: o ponto no meio da passagem mais longa pela cidade. Empate
 * resolve pela passagem mais cedo (determinismo).
 * Sem nenhuma passagem interna: o ponto da rota mais próximo do centroide, se
 * estiver a até `MAX_OFF_ROUTE_FALLBACK_KM`. Sem rota utilizável ou fallback
 * longe demais: `null` (o chamador mantém o centroide).
 */
export function cityStopPointOnPath(
  centroid: LatLng,
  geometry: CityGeometry,
  path: LatLng[],
  opts: CityStopPointOptions = {}
): CityStopPoint | null {
  if (path.length < 2) return null
  if (!Number.isFinite(centroid.lat) || !Number.isFinite(centroid.lng)) return null
  if (!validGeometry(geometry)) return null

  const along = opts.along && opts.along.length === path.length ? opts.along : cumulativeKmAlong(path)
  const window = scanWindow(path, along, opts)
  if (!window) return null

  const spans = mergeNearbySpans(insideSpans(window.path, geometry, window.along))

  if (spans.length > 0) {
    let best = spans[0]
    for (const span of spans) {
      if (span.lengthKm > best.lengthKm) best = span
    }
    const targetKm = (best.fromAlongKm + best.toAlongKm) / 2
    const at = pointAtKmAlong(path, targetKm)
    if (!at) return null
    return {
      lat: at.point.lat,
      lng: at.point.lng,
      alongKm: Math.round(at.alongKm * 100) / 100,
      distanceToCentroidKm:
        Math.round(haversineKm(centroid.lat, centroid.lng, at.point.lat, at.point.lng) * 100) / 100,
      insideMunicipality: true,
    }
  }

  const projection = projectOnPathKm(centroid, window.path)
  if (!projection || !Number.isFinite(projection.alongKm)) return null
  if (projection.distanceKm > MAX_OFF_ROUTE_FALLBACK_KM) return null
  return {
    lat: projection.point.lat,
    lng: projection.point.lng,
    alongKm: Math.round(projection.alongKm * 100) / 100,
    distanceToCentroidKm: Math.round(projection.distanceKm * 100) / 100,
    insideMunicipality: false,
  }
}

/**
 * Re-projeta um ponto qualquer sobre a polyline atual. É o counterpart
 * client-side de `cityStopPointOnPath`: o servidor devolve um `routePoint`
 * calculado sobre a rota base (origem → destino), mas depois que outras
 * paradas viram waypoints a linha muda, e o pin precisa ficar em cima da linha
 * que está na tela. O polígono do município não existe no browser, então aqui
 * a operação é só "gruda na linha mais próxima".
 */
export function snapPointToPath(point: LatLng, path: LatLng[]): LatLng | null {
  if (path.length < 2) return null
  if (!Number.isFinite(point.lat) || !Number.isFinite(point.lng)) return null
  const projection = projectOnPathKm(point, path)
  if (!projection) return null
  return projection.point
}
